import { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { ChevronLeft, ChevronRight, ChevronDown, Plus, Edit, Copy, Trash2, CheckCircle2, Circle, LogIn, LogOut, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { MonthPicker } from "@/components/ui/month-picker";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import MainLayout from "@/components/layouts";
import {
  getDailyWork,
  getDailyWorkDays,
  getDailyWorkLogs,
  deleteDailyWorkDay,
  listDailyWorkExecutionsByDay,
  markDailyWorkDayOffSiteByVendor,
  resolveDailyWorkPayoutHours,
} from "@/app/vendors/daily-work/daily-work.api";
import { DailyWorkJoin, DailyWorkDayJoin, DailyWorkLogJoin } from "@/repositories/daily-work.repository";
import { DailyWorkExecutionJoin } from "@/repositories/daily-work-execution.repository";
import { InvoicesRepository } from "@/repositories/invoices.repository";
import type { InvoiceListJoin } from "@/repositories/invoice-records";
import { formatPeriodLabel } from "@/lib/invoices/invoice-subject";
import { getBillingPeriodOverview } from "@/lib/invoices/billing-calendar";
import { daily_work_status, daily_work_day_status } from "@/models/daily-work.model";
import moment from "moment";
import DayConfigurationModal from "@/app/vendors/daily-work/components/DayConfigurationModal";
import PayoutReviewNotice from "@/app/vendors/daily-work/components/PayoutReviewNotice";
import MarkOffSiteForm from "@/app/vendors/daily-work/components/MarkOffSiteForm";
import {
  format,
  isWithinInterval,
  parseISO,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameDay,
  startOfWeek,
  endOfWeek,
  addMonths,
  subMonths,
  addDays,
  subDays,
  addWeeks,
  subWeeks,
  getDay,
  isPast,
  startOfDay,
} from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type CalendarView = "day" | "week" | "month";

const CALENDAR_VIEW_STORAGE_KEY = "daily-work-calendar-view";

export default function DailyWorkDetailPage() {
  const { dailyWorkId } = useParams<{ dailyWorkId: string }>();
  const [loading, setLoading] = useState(true);
  const [daysLoading, setDaysLoading] = useState(false);
  const [dailyWork, setDailyWork] = useState<DailyWorkJoin | null>(null);
  const [days, setDays] = useState<DailyWorkDayJoin[]>([]);
  const [logs, setLogs] = useState<DailyWorkLogJoin[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [copyDay, setCopyDay] = useState<DailyWorkDayJoin | null>(null);
  const [isCopyMode, setIsCopyMode] = useState(false);

  // View state with localStorage persistence
  const [view, setView] = useState<CalendarView>(() => {
    const saved = localStorage.getItem(CALENDAR_VIEW_STORAGE_KEY);
    return (saved as CalendarView) || "month";
  });

  // Save view to localStorage when it changes
  useEffect(() => {
    localStorage.setItem(CALENDAR_VIEW_STORAGE_KEY, view);
  }, [view]);

  // Get current month and year
  const currentMonth = moment().toDate();
  const [currentViewDate, setCurrentViewDate] = useState(currentMonth);
  const [selectedDate, setSelectedDate] = useState<Date>(currentMonth);
  const [selectedDay, setSelectedDay] = useState<DailyWorkDayJoin | null>(null);
  const [dayDetailsModalOpen, setDayDetailsModalOpen] = useState(false);
  const [selectedDayForDetails, setSelectedDayForDetails] = useState<DailyWorkDayJoin | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [detailExecutions, setDetailExecutions] = useState<DailyWorkExecutionJoin[]>([]);
  const [detailExecLoading, setDetailExecLoading] = useState(false);
  const [contractInvoiceLoad, setContractInvoiceLoad] = useState<{
    dailyWorkId: string;
    invoices: InvoiceListJoin[];
  } | null>(null);
  const contractInvoices =
    contractInvoiceLoad?.dailyWorkId === dailyWorkId ? contractInvoiceLoad.invoices : null;

  useEffect(() => {
    if (!dailyWorkId) return;
    let cancelled = false;
    InvoicesRepository.listByDailyWorkId(dailyWorkId)
      .then((rows) => {
        if (!cancelled) setContractInvoiceLoad({ dailyWorkId, invoices: rows });
      })
      .catch(() => {
        if (!cancelled) setContractInvoiceLoad({ dailyWorkId, invoices: [] });
      });
    return () => {
      cancelled = true;
    };
  }, [dailyWorkId, logs]);

  // Calendar-month billing period (SHY-182): the running month, and the date
  // the cron drafts its invoice (3rd of the following month).
  const billingPeriod = useMemo(() => getBillingPeriodOverview(), []);

  // Load initial data when dailyWorkId changes
  useEffect(() => {
    if (!dailyWorkId) return;

    const fetchInitialData = async () => {
      try {
        setLoading(true);
        const [workData, logsData] = await Promise.all([getDailyWork(dailyWorkId), getDailyWorkLogs(dailyWorkId)]);
        setDailyWork(workData);
        setLogs(logsData);
      } catch (err: any) {
        setError(err.message || "Failed to load daily work");
      } finally {
        setLoading(false);
      }
    };

    fetchInitialData();
  }, [dailyWorkId]);

  // When daily work is loaded, show calendar at contract start date (not today)
  useEffect(() => {
    if (!dailyWork?.start_date) return;
    const start = parseISO(dailyWork.start_date);
    setCurrentViewDate(start);
    setSelectedDate(start);
  }, [dailyWork?.id, dailyWork?.start_date]);

  // Load days when view date changes (but not when selectedDate changes)
  useEffect(() => {
    if (!dailyWorkId || !dailyWork) return;

    const fetchDays = async () => {
      try {
        setDaysLoading(true);

        // Load days for the visible range
        if (dailyWork.start_date && dailyWork.end_date) {
          let loadStart: Date;
          let loadEnd: Date;

          if (view === "day") {
            loadStart = selectedDate;
            loadEnd = selectedDate;
          } else if (view === "week") {
            const weekStart = startOfWeek(currentViewDate, { weekStartsOn: 1 });
            const weekEnd = endOfWeek(currentViewDate, { weekStartsOn: 1 });
            loadStart = weekStart;
            loadEnd = weekEnd;
          } else {
            // month
            loadStart = startOfMonth(currentViewDate);
            loadEnd = endOfMonth(currentViewDate);
          }

          const contractStart = parseISO(dailyWork.start_date);
          const contractEnd = parseISO(dailyWork.end_date);

          // Bound by contract dates
          const actualStart = contractStart > loadStart ? contractStart : loadStart;
          const actualEnd = contractEnd < loadEnd ? contractEnd : loadEnd;

          if (actualStart <= actualEnd) {
            const daysData = await getDailyWorkDays(
              dailyWorkId,
              format(actualStart, "yyyy-MM-dd"),
              format(actualEnd, "yyyy-MM-dd")
            );
            setDays(daysData);
          } else {
            setDays([]);
          }
        } else {
          // If no date range, load all days
          const daysData = await getDailyWorkDays(dailyWorkId);
          setDays(daysData);
        }
      } catch (err: any) {
        console.error("Failed to load days:", err);
      } finally {
        setDaysLoading(false);
      }
    };

    fetchDays();
  }, [dailyWorkId, currentViewDate, view, dailyWork]);

  // Update selected day when selectedDate changes (no data fetch, just local state)
  useEffect(() => {
    if (selectedDate) {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const day = days.find((d) => d.work_date === dateStr) || null;
      setSelectedDay(day);
    }
  }, [selectedDate, days]);

  // Load executions when details modal opens
  useEffect(() => {
    if (!selectedDayForDetails) {
      setDetailExecutions([]);
      return;
    }
    let cancelled = false;
    setDetailExecLoading(true);
    listDailyWorkExecutionsByDay(selectedDayForDetails.id)
      .then((data) => {
        if (!cancelled) setDetailExecutions(data);
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setDetailExecLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedDayForDetails?.id]);

  const formatTime = (time: string | null) => {
    if (!time) return null;
    const [h, m] = time.split(":");
    const hour = parseInt(h);
    const ampm = hour >= 12 ? "PM" : "AM";
    const hour12 = hour % 12 || 12;
    return `${hour12}:${m} ${ampm}`;
  };

  const getStatusBadge = (status: daily_work_status) => {
    const variants: Record<daily_work_status, "default" | "secondary" | "destructive"> = {
      [daily_work_status.active]: "default",
      [daily_work_status.paused]: "secondary",
      [daily_work_status.cancelled]: "destructive",
    };
    return <Badge variant={variants[status]}>{status}</Badge>;
  };

  const getDayStatusBadge = (status: daily_work_day_status) => {
    const config: Record<daily_work_day_status, { variant: "default" | "secondary" | "outline"; className: string }> = {
      [daily_work_day_status.pending]: { variant: "secondary", className: "bg-gray-100 text-gray-700" },
      [daily_work_day_status.assigned]: { variant: "secondary", className: "bg-blue-100 text-blue-700" },
      [daily_work_day_status.accepted]: { variant: "secondary", className: "bg-yellow-100 text-yellow-700" },
      [daily_work_day_status.inprogress]: { variant: "secondary", className: "bg-orange-100 text-orange-700" },
      [daily_work_day_status.completed]: { variant: "secondary", className: "bg-green-100 text-green-700" },
    };
    const { variant, className } = config[status] ?? config[daily_work_day_status.pending];
    return <Badge variant={variant} className={className}>{status}</Badge>;
  };

  const getAppliedRate = (day: DailyWorkDayJoin): number => {
    if (day.assigned_org && day.subs_payment != null) return day.subs_payment;
    return day.individual_payment ?? 0;
  };

  const getAppliedRateLabel = (day: DailyWorkDayJoin): string => {
    const source = day.assigned_org ? day.subs_payment_source : day.individual_payment_source;
    if (source === "profile") return "Profile rate";
    if (source === "manual") return "Manual override";
    return "Applied rate";
  };

  const getDayStatusColors = (status: daily_work_day_status, isSelected: boolean) => {
    if (isSelected) return "bg-background/90 text-foreground border border-background/50";
    const colors: Record<daily_work_day_status, string> = {
      [daily_work_day_status.pending]: "bg-gray-100 text-gray-700 border border-gray-300",
      [daily_work_day_status.assigned]: "bg-blue-100 text-blue-700 border border-blue-300",
      [daily_work_day_status.accepted]: "bg-yellow-100 text-yellow-700 border border-yellow-300",
      [daily_work_day_status.inprogress]: "bg-orange-100 text-orange-700 border border-orange-300",
      [daily_work_day_status.completed]: "bg-green-100 text-green-700 border border-green-300",
    };
    return colors[status] ?? colors[daily_work_day_status.pending];
  };

  // Get day configuration for a specific date
  const getDayForDate = (date: Date): DailyWorkDayJoin | null => {
    const dateStr = format(date, "yyyy-MM-dd");
    return days.find((d) => d.work_date === dateStr) || null;
  };

  // Check if a date is within the contract range
  const isDateInRange = (date: Date): boolean => {
    if (!dailyWork?.start_date || !dailyWork?.end_date) return false;
    const start = parseISO(dailyWork.start_date);
    const end = parseISO(dailyWork.end_date);
    return isWithinInterval(date, { start, end });
  };

  // Get dates that have configurations
  const configuredDates = useMemo(() => {
    return days.map((d) => parseISO(d.work_date));
  }, [days]);

  const handleDateClick = (date: Date) => {
    if (!isDateInRange(date)) {
      return;
    }
    setSelectedDate(date);
    setSelectedDay(null); // Always open in add mode; use day card Edit to edit
    setDayModalOpen(true);
  };

  const handleCalendarDayClick = (date: Date) => {
    if (!isDateInRange(date)) {
      return;
    }
    setSelectedDate(date);
  };

  const handleBadgeClick = (dayConfig: DailyWorkDayJoin, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedDayForDetails(dayConfig);
    setDayDetailsModalOpen(true);
  };

  const handleEditDay = (dayConfig: DailyWorkDayJoin) => {
    const date = parseISO(dayConfig.work_date);
    setSelectedDate(date);
    setSelectedDay(dayConfig);
    setIsCopyMode(false);
    setCopyDay(null);
    setDayDetailsModalOpen(false);
    setDayModalOpen(true);
  };

  const handleCopyDay = (dayConfig: DailyWorkDayJoin) => {
    setCopyDay(dayConfig);
    setIsCopyMode(true);
    setSelectedDay(null);
    // Set selected date to the copied day's date initially, but user can change it
    const date = parseISO(dayConfig.work_date);
    setSelectedDate(date);
    setDayDetailsModalOpen(false);
    setDayModalOpen(true);
  };

  const handleDeleteDay = async (dayConfig: DailyWorkDayJoin) => {
    setDeleting(true);
    try {
      await deleteDailyWorkDay(dayConfig.id);
      toast.success("Day configuration deleted");
      setDayDetailsModalOpen(false);
      setSelectedDayForDetails(null);
      setDeleteConfirm(false);
      // Refetch days
      await handleDayConfigSuccess();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete day configuration");
    } finally {
      setDeleting(false);
    }
  };

  // A day changed server-side: keep the open modal, the day-view selection,
  // and the calendar in step.
  const applyUpdatedDay = (updatedDay: DailyWorkDayJoin) => {
    setSelectedDayForDetails(updatedDay);
    setSelectedDay((current) => (current?.id === updatedDay.id ? updatedDay : current));
    setDays((current) => current.map((day) => (day.id === updatedDay.id ? updatedDay : day)));
  };

  const handleResolvePayoutHours = async (hoursWorked: number) => {
    if (!selectedDayForDetails) return;

    const resolvedDay = await resolveDailyWorkPayoutHours(selectedDayForDetails.id, hoursWorked);
    applyUpdatedDay(resolvedDay);
    toast.success("Payout hours approved");
  };

  const handleMarkOffSite = async (input: { clockOut: string; note: string | null }) => {
    if (!selectedDayForDetails || !dailyWorkId) return;

    try {
      const result = await markDailyWorkDayOffSiteByVendor(selectedDayForDetails, input);
      applyUpdatedDay(result.day);
      setDetailExecutions(result.execution ? [result.execution] : []);
      getDailyWorkLogs(dailyWorkId).then(setLogs).catch(() => {});
      toast.success("Day marked off-site");
    } catch (error: any) {
      toast.error(error.message || "Failed to mark off-site");
      throw error;
    }
  };

  const handleDayConfigSuccess = async () => {
    setDayModalOpen(false);
    // Refetch days for the current view
    if (dailyWorkId && dailyWork) {
      try {
        setDaysLoading(true);
        let loadStart: Date;
        let loadEnd: Date;

        if (view === "day") {
          loadStart = selectedDate;
          loadEnd = selectedDate;
        } else if (view === "week") {
          const weekStart = startOfWeek(currentViewDate, { weekStartsOn: 1 });
          const weekEnd = endOfWeek(currentViewDate, { weekStartsOn: 1 });
          loadStart = weekStart;
          loadEnd = weekEnd;
        } else {
          loadStart = startOfMonth(currentViewDate);
          loadEnd = endOfMonth(currentViewDate);
        }

        const contractStart = parseISO(dailyWork.start_date);
        const contractEnd = parseISO(dailyWork.end_date);

        const actualStart = contractStart > loadStart ? contractStart : loadStart;
        const actualEnd = contractEnd < loadEnd ? contractEnd : loadEnd;

        if (actualStart <= actualEnd) {
          const daysData = await getDailyWorkDays(
            dailyWorkId,
            format(actualStart, "yyyy-MM-dd"),
            format(actualEnd, "yyyy-MM-dd")
          );
          setDays(daysData);

          // Update selected day after refresh
          const dateStr = format(selectedDate, "yyyy-MM-dd");
          const day = daysData.find((d) => d.work_date === dateStr) || null;
          setSelectedDay(day);
        }
      } catch (err) {
        console.error("Failed to refresh days:", err);
      } finally {
        setDaysLoading(false);
      }
    }
  };

  // Navigation handlers
  const handlePrevious = () => {
    if (view === "day") {
      const newDate = subDays(selectedDate, 1);
      setSelectedDate(newDate);
      setCurrentViewDate(newDate);
    } else if (view === "week") {
      const newDate = subWeeks(currentViewDate, 1);
      setCurrentViewDate(newDate);
      setSelectedDate(newDate);
    } else {
      // month
      const newMonth = subMonths(currentViewDate, 1);
      setCurrentViewDate(newMonth);
    }
  };

  const handleNext = () => {
    if (view === "day") {
      const newDate = addDays(selectedDate, 1);
      setSelectedDate(newDate);
      setCurrentViewDate(newDate);
    } else if (view === "week") {
      const newDate = addWeeks(currentViewDate, 1);
      setCurrentViewDate(newDate);
      setSelectedDate(newDate);
    } else {
      // month
      const newMonth = addMonths(currentViewDate, 1);
      setCurrentViewDate(newMonth);
    }
  };

  // Handle date selection from dropdown (day view)
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    setCurrentViewDate(date);
    setSelectedDate(date);
  };

  // Handle month/year selection from dropdown (week and month views)
  const handleMonthYearSelect = (date: Date | undefined) => {
    if (!date) return;
    if (view === "week") {
      const firstDayOfMonth = startOfMonth(date);
      const weekStart = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
      setCurrentViewDate(weekStart);
      setSelectedDate(weekStart);
    } else {
      setCurrentViewDate(date);
    }
  };

  // Calculate week days (Mon-Sun)
  const getWeekDays = () => {
    const weekStart = startOfWeek(currentViewDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(currentViewDate, { weekStartsOn: 1 });
    return eachDayOfInterval({ start: weekStart, end: weekEnd });
  };

  // Calculate month days
  const getMonthDays = () => {
    const monthStart = startOfMonth(currentViewDate);
    const monthEnd = endOfMonth(currentViewDate);
    const daysInMonth = eachDayOfInterval({ start: monthStart, end: monthEnd });
    const firstDayOfMonth = getDay(monthStart);
    const previousMonthDays = [];

    // Adjust for Monday start (1 = Monday, 0 = Sunday)
    const offset = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

    for (let i = 0; i < offset; i++) {
      previousMonthDays.push(null);
    }

    return [...previousMonthDays, ...daysInMonth];
  };

  // Get title based on view
  const getTitle = () => {
    if (view === "day") {
      return format(selectedDate, "EEEE, MMMM d, yyyy");
    } else if (view === "week") {
      return format(currentViewDate, "MMMM yyyy");
    } else {
      return format(currentViewDate, "MMMM yyyy");
    }
  };

  // Get days for a specific date
  const getDaysForDate = (date: Date): DailyWorkDayJoin[] => {
    const dateStr = format(date, "yyyy-MM-dd");
    return days.filter((d) => d.work_date === dateStr);
  };

  // Render Day View
  const renderDayView = () => {
    const dayDays = getDaysForDate(selectedDate);

    return (
      <div className="space-y-4">
        {dayDays.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            <p>No daily work configured for {format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
            {isDateInRange(selectedDate) && (
              <Button
                className="mt-4"
                onClick={() => handleDateClick(selectedDate)}
                data-testid="daily-work-configure-selected-day-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Configure This Day
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-3">
            {dayDays.map((day) => (
              <div key={day.id} className="p-4 border rounded-lg hover:bg-accent transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="font-semibold">{day.service.name}</span>
                      {getDayStatusBadge(day.status)}
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      {day.tasks.length} task{day.tasks.length !== 1 ? "s" : ""}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {day.assigned_user?.name || day.assigned_org?.display_name || "No assignment"}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {getAppliedRateLabel(day)}: ${getAppliedRate(day).toFixed(2)}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedDay(day);
                      setDayModalOpen(true);
                    }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {isDateInRange(selectedDate) && (
              <Button
                variant="outline"
                className="w-full mt-2"
                onClick={() => handleDateClick(selectedDate)}
                data-testid="daily-work-add-another-day-button"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add another day
              </Button>
            )}
          </div>
        )}
      </div>
    );
  };

  // Render Week View
  const renderWeekView = () => {
    const weekDays = getWeekDays();

    return (
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day, index) => {
          if (!day) {
            return <div key={index} className="h-24" />;
          }

          const dayDays = getDaysForDate(day);
          const dayCount = dayDays.length;
          const isSelected = isSameDay(day, selectedDate);
          const isToday = isSameDay(day, moment().toDate());
          const isPastDate = isPast(startOfDay(day)) && !isToday;
          const inRange = isDateInRange(day);

          return (
            <div
              key={day.toISOString()}
              className={cn(
                "min-h-[300px] border border-border rounded-lg p-1 cursor-pointer transition-colors hover:bg-muted/50",
                isSelected && "bg-primary text-primary-foreground hover:bg-primary/70",
                isToday && !isSelected && "bg-muted",
                isPastDate && !isSelected && "opacity-50",
                !inRange && "opacity-30 cursor-not-allowed"
              )}
              onClick={() => inRange && handleCalendarDayClick(day)}
            >
              <div className="flex flex-col h-full">
                <div className="text-xs font-medium mb-1">{format(day, "d")}</div>
                {dayCount > 0 && (
                  <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                    {dayDays.slice(0, 15).map((dayConfig) => {
                      const assigneeName = dayConfig.assigned_user?.name || dayConfig.assigned_org?.display_name || "";
                      const rate = getAppliedRate(dayConfig);
                      const rateStr = rate ? `$${rate.toFixed(2)}` : "";
                      const propertyName = dailyWork?.property.name || dayConfig.daily_work_data.property.name;

                      return (
                        <div
                          key={dayConfig.id}
                          className={cn(
                            "text-xs px-1 py-0.5 rounded group relative cursor-pointer hover:opacity-80 transition-opacity",
                            getDayStatusColors(dayConfig.status, isSelected)
                          )}
                          onClick={(e) => handleBadgeClick(dayConfig, e)}
                        >
                          <div className="truncate">
                            {propertyName} - {dayConfig.service.name}
                          </div>
                          {assigneeName && (
                            <div className="truncate">
                              {assigneeName} - {rateStr}
                            </div>
                          )}
                          <div className="absolute top-0 right-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-4 w-4 p-0 bg-background/80 hover:bg-background"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyDay(dayConfig);
                              }}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                    {dayCount > 15 && <div className="text-xs text-muted-foreground">+{dayCount - 15} more</div>}
                  </div>
                )}
                {inRange && (
                  <div className={cn("flex items-center justify-center", dayCount === 0 && "flex-1")}>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDateClick(day);
                      }}
                    >
                      <Plus className="h-3 w-3 mr-1" />
                      Add
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // Render Month View
  const renderMonthView = () => {
    const allDays = getMonthDays();

    return (
      <>
        <div className="grid grid-cols-7 gap-1 mb-2">
          {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
            <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
              {day}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {allDays.map((day, index) => {
            if (!day) {
              return <div key={index} className="min-h-[72px]" />;
            }

            const dayDays = getDaysForDate(day);
            const dayCount = dayDays.length;
            const isSelected = isSameDay(day, selectedDate);
            const isToday = isSameDay(day, moment().toDate());
            const isPastDate = isPast(startOfDay(day)) && !isToday;
            const inRange = isDateInRange(day);

            return (
              <div
                key={day.toISOString()}
                className={cn(
                  "min-h-[72px] border border-border rounded-lg p-1 cursor-pointer transition-colors hover:bg-muted/50",
                  isSelected && "bg-primary text-primary-foreground hover:bg-primary/70",
                  isToday && !isSelected && "bg-muted",
                  isPastDate && !isSelected && "opacity-50",
                  !inRange && "opacity-30 cursor-not-allowed"
                )}
                onClick={() => inRange && handleCalendarDayClick(day)}
              >
                <div className="flex flex-col h-full">
                  <div className="text-xs font-medium mb-1">{format(day, "d")}</div>
                  {dayCount > 0 && (
                    <div className="flex-1 flex flex-col gap-0.5 overflow-hidden">
                      {dayDays.slice(0, 3).map((dayConfig) => {
                        const assigneeName =
                          dayConfig.assigned_user?.name || dayConfig.assigned_org?.display_name || "";
                        const rate = getAppliedRate(dayConfig);
                        const rateStr = rate ? `$${rate.toFixed(2)}` : "";
                        const propertyName = dailyWork?.property.name || dayConfig.daily_work_data.property.name;

                        return (
                          <div
                            key={dayConfig.id}
                            className={cn(
                              "text-xs px-1 py-0.5 rounded group relative cursor-pointer hover:opacity-80 transition-opacity",
                              getDayStatusColors(dayConfig.status, isSelected)
                            )}
                            onClick={(e) => handleBadgeClick(dayConfig, e)}
                          >
                            <div className="truncate">
                              {propertyName} - {dayConfig.service.name}
                            </div>
                            {assigneeName && (
                              <div className="truncate">
                                {assigneeName} - {rateStr}
                              </div>
                            )}
                            <div className="absolute top-0 right-0 flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-4 w-4 p-0 bg-background/80 hover:bg-background"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCopyDay(dayConfig);
                                }}
                              >
                                <Copy className="h-3 w-3" />
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                      {dayCount > 3 && <div className="text-xs text-muted-foreground">+{dayCount - 3} more</div>}
                    </div>
                  )}
                  {inRange && (
                    <div className={cn("flex items-center justify-center", dayCount === 0 && "flex-1")}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDateClick(day);
                        }}
                      >
                        <Plus className="h-3 w-3 mr-1" />
                        Add
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  };

  const breadcrumb = [
    { name: "Daily Work", href: "/dailywork", current: false },
    { name: dailyWork ? dailyWork.property.name : "Loading...", href: "#", current: true },
  ];

  if (loading) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      </MainLayout>
    );
  }

  if (error || !dailyWork) {
    return (
      <MainLayout breadcrumb={breadcrumb}>
        <div className="flex items-center justify-center h-64">
          <p className="text-destructive">{error ?? "Daily work not found"}</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout breadcrumb={breadcrumb}>
      <div className="space-y-6">
        {/* Overview Card - Smaller */}
        <Card>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Info label="Property">{dailyWork.property.name}</Info>
              <Info label="Status">{getStatusBadge(dailyWork.status)}</Info>
              <Info label="Start Date">
                {dailyWork.start_date ? moment(dailyWork.start_date).format("MM/DD/YYYY") : "Not set"}
              </Info>
              <Info label="End Date">
                {dailyWork.end_date ? moment(dailyWork.end_date).format("MM/DD/YYYY") : "Ongoing"}
              </Info>
              <Info label="Monthly Price">
                {dailyWork.monthly_price === null || dailyWork.monthly_price === undefined ? (
                  <span className="text-muted-foreground">Not billed automatically</span>
                ) : (
                  `$${dailyWork.monthly_price.toFixed(2)}`
                )}
              </Info>
              <Info label="Current Period">{billingPeriod.currentRange}</Info>
              <Info label="Next Invoice">
                {dailyWork.monthly_price === null || dailyWork.monthly_price === undefined ? (
                  <span className="text-muted-foreground">Set a monthly price</span>
                ) : (
                  <>
                    {billingPeriod.nextInvoiceDate}
                    <span className="block text-xs font-normal text-muted-foreground">
                      for {billingPeriod.currentLabel}
                    </span>
                  </>
                )}
              </Info>
            </div>
          </CardContent>
        </Card>

        {/* Period invoices */}
        <Card data-testid="daily-work-invoices-card">
          <CardHeader>
            <CardTitle className="uppercase">Invoices</CardTitle>
          </CardHeader>
          <CardContent>
            {contractInvoices === null ? (
              <p className="text-sm text-muted-foreground">Loading invoices…</p>
            ) : contractInvoices.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                {dailyWork.monthly_price === null || dailyWork.monthly_price === undefined
                  ? "No invoices yet. Set a monthly price to draft one automatically after each month ends."
                  : "No invoices yet. The first draft is generated a few days after the current period ends."}
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="py-2 font-semibold">Period</th>
                    <th className="py-2 font-semibold">Invoice</th>
                    <th className="py-2 font-semibold">Issued</th>
                    <th className="py-2 font-semibold text-right">Amount</th>
                    <th className="py-2 font-semibold text-right">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {contractInvoices.map((invoice) => (
                    <tr key={invoice.id} className="border-b last:border-b-0">
                      <td className="py-2 font-medium">{formatPeriodLabel(invoice.period_start) ?? "—"}</td>
                      <td className="py-2">
                        <Link to={`/invoices/${invoice.id}`} className="text-primary hover:underline">
                          #{invoice.invoice_number}
                        </Link>
                      </td>
                      <td className="py-2 text-muted-foreground">
                        {invoice.issue_date ? moment(invoice.issue_date).format("MM/DD/YYYY") : "—"}
                      </td>
                      <td className="py-2 text-right">${invoice.amount.toFixed(2)}</td>
                      <td className="py-2 text-right">
                        <Badge variant="outline" className="uppercase">
                          {invoice.status}
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Calendar View - Full Width */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="ghost"
                    className="h-auto p-0 font-semibold text-lg hover:bg-transparent"
                    title={view === "day" ? format(selectedDate, "PPP") : format(currentViewDate, "MMM yyyy")}
                  >
                    <div className="flex items-center gap-2">
                      <CardTitle className="mb-0">{getTitle()}</CardTitle>
                      <ChevronDown className="h-4 w-4" />
                    </div>
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start" side="bottom">
                  {view === "day" ? (
                    <Calendar mode="single" selected={selectedDate} onSelect={handleDateSelect} initialFocus />
                  ) : (
                    <MonthPicker selectedMonth={currentViewDate} onMonthSelect={handleMonthYearSelect} />
                  )}
                </PopoverContent>
              </Popover>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handlePrevious}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleNext}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Select value={view} onValueChange={(value) => setView(value as CalendarView)}>
                  <SelectTrigger className="w-[120px]" data-testid="daily-work-detail-view-trigger">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="day">Day</SelectItem>
                    <SelectItem value="week">Week</SelectItem>
                    <SelectItem value="month">Month</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="relative">
            {daysLoading && (
              <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            )}
            {view === "day" && renderDayView()}
            {view === "week" && (
              <>
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                    <div key={day} className="p-2 text-center text-sm font-medium text-muted-foreground">
                      {day}
                    </div>
                  ))}
                </div>
                {renderWeekView()}
              </>
            )}
            {view === "month" && renderMonthView()}
          </CardContent>
        </Card>

        {/* Activity Logs */}
        <Card>
          <CardHeader>
            <CardTitle className="uppercase">Activity Log</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {logs.length === 0 ? (
              <p className="text-sm text-muted-foreground">No activity logs yet</p>
            ) : (
              logs.map((log) => (
                <div key={log.id} className="border-l-2 border-primary pl-4 relative">
                  <span className="absolute -left-[7px] top-[6px] w-3 h-3 rounded-full bg-primary" />
                  <div className="flex justify-between items-center">
                    <h4 className="font-semibold text-primary">{moment(log.created_at).format("MM/DD/YYYY")}</h4>
                    <time className="text-xs text-muted-foreground">{moment(log.created_at).format("h:mm a")}</time>
                  </div>
                  <p className="font-medium">{log.message}</p>
                  {log.meta_data?.kind === "period_invoice" && (
                    <p className="text-sm text-muted-foreground">
                      {log.meta_data.days_completed ?? 0} completed day(s) · {Number(log.meta_data.hours_worked ?? 0)} hour(s)
                      {" · "}${Number(log.meta_data.amount ?? 0).toFixed(2)}
                      {log.meta_data.invoice_id && (
                        <>
                          {" · "}
                          <Link to={`/invoices/${log.meta_data.invoice_id}`} className="text-primary hover:underline">
                            View invoice
                          </Link>
                        </>
                      )}
                    </p>
                  )}
                  {log.meta_data?.kind === "period_invoice_error" && (
                    <p className="text-sm text-destructive">{log.meta_data.error}</p>
                  )}
                  {log.user?.name && <p className="text-sm text-muted-foreground">By {log.user.name}</p>}
                </div>
              ))
            )}
          </CardContent>
        </Card>
      </div>

      {dayModalOpen && selectedDate && dailyWork && (
        <DayConfigurationModal
          open={dayModalOpen}
          onOpenChange={(open) => {
            setDayModalOpen(open);
            if (!open) {
              setIsCopyMode(false);
              setCopyDay(null);
            }
          }}
          dailyWork={dailyWork}
          workDate={format(selectedDate, "yyyy-MM-dd")}
          existingDay={isCopyMode ? null : selectedDay}
          copyDay={isCopyMode ? copyDay : null}
          onSuccess={handleDayConfigSuccess}
        />
      )}

      {dayDetailsModalOpen && selectedDayForDetails && dailyWork && (
        <Dialog open={dayDetailsModalOpen} onOpenChange={(open) => {
          setDayDetailsModalOpen(open);
          if (!open) setDeleteConfirm(false);
        }}>
          <DialogContent className="sm:max-w-[400px]">
            <DialogHeader>
              <DialogTitle>{dailyWork.property.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex items-center gap-2">
                <p className="text-sm font-medium">{selectedDayForDetails.service.name}</p>
                {getDayStatusBadge(selectedDayForDetails.status)}
                {selectedDayForDetails.recurring && <Badge variant="outline">Recurring</Badge>}
              </div>
              {selectedDayForDetails.duration_label && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Duration</p>
                  <p className="font-medium">{selectedDayForDetails.duration_label}</p>
                </div>
              )}
              {selectedDayForDetails.instructions && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Instructions</p>
                  <p className="whitespace-pre-wrap">{selectedDayForDetails.instructions}</p>
                </div>
              )}
              {selectedDayForDetails.assigned_user?.name || selectedDayForDetails.assigned_org?.display_name ? (
                <div>
                  <p className="text-xs text-muted-foreground">Assigned to</p>
                  <p className="text-sm font-medium">
                    {selectedDayForDetails.assigned_user?.name || selectedDayForDetails.assigned_org?.display_name}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {getAppliedRateLabel(selectedDayForDetails)}: ${getAppliedRate(selectedDayForDetails).toFixed(2)}
                  </p>
                </div>
              ) : null}
              {selectedDayForDetails.max_hours != null && (
                <div className="text-sm">
                  <p className="text-xs text-muted-foreground">Max hours</p>
                  <p className="font-medium">{selectedDayForDetails.max_hours}</p>
                </div>
              )}
              <div>
                <p className="text-xs text-muted-foreground mb-2">Tasks</p>
                <div className="space-y-1.5">
                  {selectedDayForDetails.tasks.map((task, idx) => {
                    const latestExec = detailExecutions.length > 0 ? detailExecutions[0] : null;
                    const execTask = latestExec?.tasks?.find((t) => t.name === task);
                    const isDone = execTask?.done ?? false;
                    return (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {isDone ? (
                          <CheckCircle2 className="h-4 w-4 text-green-600 flex-shrink-0" />
                        ) : (
                          <Circle className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                        )}
                        <span className={cn(isDone && "line-through text-muted-foreground")}>{task}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Clock In/Out */}
              {detailExecLoading ? (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading execution data...
                </div>
              ) : detailExecutions.length > 0 && (
                <div>
                  <p className="text-xs text-muted-foreground mb-2">On-site / Off-site</p>
                  <div className="space-y-1.5">
                    {detailExecutions.map((exec) => (
                      <div key={exec.id} className="flex items-center gap-3 text-sm">
                        {exec.clock_in && (
                          <div className="flex items-center gap-1.5 text-green-700">
                            <LogIn className="h-3.5 w-3.5" />
                            <span>{formatTime(exec.clock_in)}</span>
                          </div>
                        )}
                        {exec.clock_out && (
                          <div className="flex items-center gap-1.5 text-red-700">
                            <LogOut className="h-3.5 w-3.5" />
                            <span>{formatTime(exec.clock_out)}</span>
                          </div>
                        )}
                        {!exec.clock_in && !exec.clock_out && (
                          <span className="text-muted-foreground">No on-site / off-site times</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {!detailExecLoading && (
                <MarkOffSiteForm
                  execution={detailExecutions[0]}
                  dayStatus={selectedDayForDetails.status}
                  maxHours={selectedDayForDetails.max_hours}
                  onConfirm={handleMarkOffSite}
                />
              )}
              <PayoutReviewNotice
                day={selectedDayForDetails}
                onResolveHours={handleResolvePayoutHours}
              />
            </div>
            <div className="flex gap-2 pt-4 border-t">
              <Button variant="outline" className="flex-1" onClick={() => handleEditDay(selectedDayForDetails)}>
                <Edit className="h-4 w-4 mr-2" />
                Edit
              </Button>
              <Button variant="default" className="flex-1" onClick={() => handleCopyDay(selectedDayForDetails)}>
                <Copy className="h-4 w-4 mr-2" />
                Copy
              </Button>
              <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" className="flex-1">
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this day configuration?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {selectedDayForDetails?.recurring
                        ? "This date will be skipped by the Master Schedule — it will not be regenerated. This action cannot be undone."
                        : "This action cannot be undone."}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      disabled={deleting}
                      onClick={() => handleDeleteDay(selectedDayForDetails)}
                    >
                      {deleting ? "Deleting..." : "Confirm"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </MainLayout>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-semibold text-muted-foreground uppercase">{label}</h3>
      <p className="text-base font-medium leading-snug">{children}</p>
    </div>
  );
}
